/**
 * W-31a › Depo detayı — the three pieces of logic the locations table and the
 * "Toplu lokasyon ekle" drawer need, kept out of the screen so they can be
 * tested and so mobile can reuse them.
 *
 * `planBulkLocations` mirrors `public.create_locations_bulk`
 * (migration 20260923000001): same prefix, same `lpad`, same 200-row ceiling,
 * existing codes skipped. The RPC stays the authority — this is what the drawer
 * previews before the click.
 */
import { D } from '../money';

// ── per-location balances (v_stock_by_location) ─────────────────────────────
/** A row of `v_stock_by_location`; every column of a view is nullable in the generated types. */
export interface LocationBalanceRow {
  location_id: string | null;
  item_id: string | null;
  qty_base: number | string | null;
  qty_stock_unit: number | string | null;
  stock_unit: string | null;
  lot_count: number | null;
}

export interface LocationStock {
  /** items whose balance in this location is not zero */
  items: number;
  /** lots behind those balances */
  lots: number;
  /** at least one item is short here — the row gets the "negatif" chip */
  negative: boolean;
  /**
   * The only item's quantity and unit when the location holds exactly one, which
   * is what the guard band quotes ("D1-A-02’de 1 üründe stok var (420 koli)").
   * Two items in different units cannot be added up, so it stays null.
   */
  singleQty: string | null;
  singleUnit: string | null;
}

export interface WarehouseStock {
  byLocation: Record<string, LocationStock>;
  /** distinct items with a non-zero balance anywhere in the warehouse */
  items: number;
  lots: number;
}

const EMPTY_LOCATION: LocationStock = { items: 0, lots: 0, negative: false, singleQty: null, singleUnit: null };

export function emptyLocationStock(): LocationStock {
  return EMPTY_LOCATION;
}

/**
 * Rolls the (item × location) rows up to one figure per location and one per
 * warehouse. A zero balance row is history, not stock: the view keeps it so the
 * item card can show "0 koli", the Kalem column must not count it.
 */
export function groupLocationStock(rows: LocationBalanceRow[]): WarehouseStock {
  const byLocation: Record<string, LocationStock> = {};
  const items = new Set<string>();
  let lots = 0;

  for (const r of rows) {
    if (!r.location_id) continue;
    const qty = D(r.qty_base ?? 0);
    if (qty.isZero()) continue;
    const rowLots = Number(r.lot_count ?? 0) || 0;
    const current = byLocation[r.location_id] ?? { items: 0, lots: 0, negative: false, singleQty: null, singleUnit: null };
    byLocation[r.location_id] = {
      items: current.items + 1,
      lots: current.lots + rowLots,
      negative: current.negative || qty.lessThan(0),
      // the first row wins while there is only one; a second row clears it
      singleQty: current.items === 0 ? D(r.qty_stock_unit ?? qty).toString() : null,
      singleUnit: current.items === 0 ? r.stock_unit : null,
    };
    if (r.item_id) items.add(r.item_id);
    lots += rowLots;
  }

  return { byLocation, items: items.size, lots };
}

// ── last count per location (v_stock_counts) ────────────────────────────────
export interface CountSummaryRow {
  count_no: string | null;
  /** `counted_at` — null while the session has not been submitted */
  at: string | null;
  location_codes: string[] | null;
}

export interface CountStamp {
  countNo: string;
  at: string;
}

export interface LastCounts {
  /** location code → the last count that touched it */
  byCode: Record<string, CountStamp>;
  /** the warehouse's own last count, for the künye row */
  last: CountStamp | null;
}

/**
 * Which count last touched each location. `v_stock_counts` carries the codes of
 * the locations a session covered, so the column needs no extra round trip.
 */
export function lastCountsByLocation(rows: CountSummaryRow[]): LastCounts {
  const dated = rows.filter(
    (r): r is CountSummaryRow & { at: string; count_no: string } =>
      typeof r.at === 'string' && r.at.length > 0 && typeof r.count_no === 'string',
  );
  // newest first, so the first row that mentions a code is the answer for it
  const sorted = [...dated].sort((a, b) => b.at.localeCompare(a.at));
  const byCode: Record<string, CountStamp> = {};
  for (const r of sorted) {
    for (const code of r.location_codes ?? []) {
      if (!byCode[code]) byCode[code] = { countNo: r.count_no, at: r.at };
    }
  }
  const first = sorted[0];
  return { byCode, last: first ? { countNo: first.count_no, at: first.at } : null };
}

// ── bulk create preview ─────────────────────────────────────────────────────
/** Mirrors the RPC's ceiling (`Tek seferde en fazla 200 lokasyon.`). */
export const BULK_LOCATIONS_MAX = 200;

export type BulkRangeError = 'number' | 'order' | 'tooLarge';

export interface BulkLocationsPlan {
  /** every code the range would ask for, in order */
  codes: string[];
  /** what the preview box writes: all of them up to four, else first three … last */
  preview: string;
  /** codes that already exist and will be skipped */
  conflicts: string[];
  /** how many rows the RPC would actually insert */
  toCreate: number;
  error: BulkRangeError | null;
  start: number;
  end: number;
  pad: number;
}

export function planBulkLocations(input: {
  prefix: string;
  start: string | number;
  end: string | number;
  pad: string | number;
  existing: Iterable<string>;
  max?: number;
}): BulkLocationsPlan {
  const start = typeof input.start === 'number' ? input.start : Number.parseInt(input.start, 10);
  const end = typeof input.end === 'number' ? input.end : Number.parseInt(input.end, 10);
  const padRaw = typeof input.pad === 'number' ? input.pad : Number.parseInt(input.pad, 10);
  const pad = Number.isFinite(padRaw) ? Math.min(Math.max(padRaw, 1), 6) : 2;
  const max = input.max ?? BULK_LOCATIONS_MAX;

  const error: BulkRangeError | null =
    !Number.isFinite(start) || !Number.isFinite(end) || start < 0
      ? 'number'
      : end < start
        ? 'order'
        : end - start + 1 > max
          ? 'tooLarge'
          : null;

  if (error) return { codes: [], preview: '—', conflicts: [], toCreate: 0, error, start, end, pad };

  const codes: string[] = [];
  for (let i = start; i <= end; i += 1) codes.push(input.prefix + String(i).padStart(pad, '0'));
  const existing = new Set(input.existing);
  const conflicts = codes.filter((c) => existing.has(c));

  return {
    codes,
    preview: codes.length <= 4 ? codes.join(' · ') : `${codes.slice(0, 3).join(' · ')} … ${codes[codes.length - 1]}`,
    conflicts,
    toCreate: codes.length - conflicts.length,
    error: null,
    start,
    end,
    pad,
  };
}
